import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import AuthBrandRail from './AuthBrandRail';
import styles from './AuthLayout.module.scss';

/**
 * AuthLayout - Two-panel layout for authentication pages
 * White form panel on LEFT, blue brand rail on RIGHT (2026+ optimal pattern)
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Form content (left panel)
 * @param {string} props.headline - Brand rail headline
 * @param {string} props.subheadline - Brand rail subheadline
 * @param {Array} props.features - Array of feature strings
 * @param {React.ReactNode} props.graphic - Optional graphic/image for brand rail
 * @param {Array} props.testimonials - Array of testimonial objects
 * @param {Object} props.metric - Optional metric object {label, value}
 * @param {string} props.variant - Blob animation variant (1-4) for unique patterns per page
 */
export const AuthLayout = ({
  children,
  headline,
  subheadline,
  features = [],
  graphic = null,
  testimonials = [],
  metric = null,
  variant = '1',
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 992);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div className={styles.authPage}>
      {/* Two-Panel Grid - NO HEADER, FULL HEIGHT */}
      <div className={styles.authGrid}>
        {/* Mobile Header (top on mobile) / Brand Rail (right on desktop) */}
        {isMobile ? (
          <div className={styles.mobileBrandHeader}>
            <Link href="/" className={styles.mobileLogoLink}>
              <Image
                src="/images/bm-logo-white.svg"
                alt="Brandymail - Intelligent Sending Domain Management"
                width={180}
                height={45}
                priority
              />
            </Link>
          </div>
        ) : (
          <AuthBrandRail
            headline={headline}
            subheadline={subheadline}
            features={features}
            graphic={graphic}
            testimonials={testimonials}
            metric={metric}
            variant={variant}
          />
        )}

        {/* LEFT PANEL: Form Content (Clean White) - Form-first 2026+ pattern */}
        <div className={styles.formPanel}>
          <div className={styles.formContent}>{children}</div>
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
